import { Link } from "react-router-dom";
import { ArrowRight, Lock, Shuffle, Trophy, ShieldCheck } from "lucide-react";
import { useSeo } from "../lib/seo";
import { Header } from "../components/Header";
import { PublicFooter, Breadcrumbs } from "../components/PublicChrome";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const STEPS = [
  [Shuffle, "The winner is picked first", "When the coach creates the game, our computer picks the winning square, duck or numbers at random – before anyone buys an entry."],
  [Lock, "It's encrypted and locked", "That result is encrypted into a code that's shown on the game page from day one. Nobody – not the coach, the club or us – can change it without the code changing too."],
  [Trophy, "The draw reveals it", "At the draw we reveal the winner and the key that unlocks the code."],
  [ShieldCheck, "Anyone can check it", "Tap “Check this result” on the game page. Your phone unlocks the code itself and confirms the winner is the same one locked in at the start."],
];

const FAQ = [
  ["Can the coach or the club change the winner?", "No. The winner was locked in before the first sale. Any change would give a different code, and the check would fail for everyone to see."],
  ["Is the winner really random?", "Yes. The computer picks it at random the moment the game is created, so nobody knows who will buy which square."],
  ["Do I have to do anything?", "No – it's there if you want it. One tap on “Check this result” after the draw is all it takes."],
  ["What if the check fails?", "That should never happen. If it does, the page tells you straight away – contact the organiser and email info@sportsshields.ie."],
  ["For the technically minded", "The code is a SHA-256 hash of the result plus a secret key. Your browser recalculates it after the draw, so you don't need to trust us."],
];

const Step = ({ icon: Icon, title, text, n }) => (
  <li className="border-2 border-slate-900 bg-white p-5 shadow-[4px_4px_0_0_#0f172a]" data-testid={`fair-step-${n}`}>
    <div className="flex items-center gap-3">
      <span className="flex h-8 w-8 items-center justify-center bg-slate-900 text-sm font-black text-lime-300">{n}</span>
      <Icon className="h-5 w-5" />
    </div>
    <h2 className="mt-3 text-base font-bold md:text-lg">{title}</h2>
    <p className="mt-1 text-sm text-slate-600">{text}</p>
  </li>
);

export default function FairDraws() {
  useSeo({ title: "How our draws are fair | Sports Shields fundraising games", description: "Every Spot the Ball, Score Squares and Duck Race winner is picked at random and encrypted before the first sale. Anyone can check the result.", path: "/fair-draws" });
  return (
    <div className="min-h-screen">
      <Header onNew={() => { window.location.href = "/design"; }} />
      <main className="mx-auto max-w-5xl space-y-10 px-4 py-10 sm:px-6">
        <Breadcrumbs items={[["Home", "/"], ["Fundraiser games", "/fundraising-games"], ["How our draws are fair", "/fair-draws"]]} />
        <section className="max-w-3xl">
          <div className="eyebrow">Provably fair</div>
          <h1 className="font-display text-4xl font-black uppercase leading-none sm:text-5xl lg:text-6xl" data-testid="fair-title">How our draws are fair</h1>
          <p className="mt-4 text-base text-slate-700 md:text-lg">The winner is chosen and encrypted before anyone buys an entry – so it can't be changed, and anyone can check it.</p>
        </section>
        <ol className="grid gap-4 sm:grid-cols-2">{STEPS.map(([icon, t, x], i) => <Step key={t} icon={icon} title={t} text={x} n={i + 1} />)}</ol>
        <section className="max-w-3xl">
          <h2 className="font-display text-2xl font-black uppercase">Questions</h2>
          <Accordion type="single" collapsible className="mt-2" data-testid="fair-faq">
            {FAQ.map(([q, a], i) => (
              <AccordionItem key={q} value={q}>
                <AccordionTrigger className="text-left font-bold" data-testid={`fair-faq-q${i}`}>{q}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
        <Link to="/fundraising-games" className="btn-blaze inline-flex items-center gap-2 px-5 py-3 text-sm" data-testid="fair-back-to-games">See the games <ArrowRight className="h-4 w-4" /></Link>
      </main>
      <PublicFooter />
    </div>
  );
}
